import {
  Header,
  HeaderGlobalAction,
  HeaderGlobalBar,
  HeaderMenu,
  HeaderMenuItem,
  HeaderName,
  HeaderNavigation,
  HeaderPanel,
  OverflowMenu,
  OverflowMenuItem,
  SkipToContent,
  Switcher,
  SwitcherItem,
  SwitcherDivider,
} from '@carbon/react';
import {
  Search,
  UserAvatar,
  Switcher as SwitcherIcon,
} from '@carbon/icons-react';
import { Icon } from './ui/icon.tsx';
import { ThemeSwitcherAction } from './theme-switcher-action.tsx';
import { UserDropdown } from './user-dropdown.tsx';
import { useOptionalUser, userHasPermission } from '../utils/user.ts';
import { Link, useNavigate, useRouteLoaderData } from 'react-router';
import { cn, getUserImgSrc } from '#app/utils/misc.tsx';
import { useState } from 'react';
import { type loader as rootLoader } from '../root.tsx';

interface CarbonHeaderProps {
  userPreference?: 'light' | 'dark' | 'system' | null;
  showMenuButton?: boolean;
  menuButton?: React.ReactNode;
  section?: string;
}

export function CarbonHeader({
  userPreference,
  showMenuButton = false,
  menuButton,
  section,
}: CarbonHeaderProps) {
  const user = useOptionalUser();
  const navigate = useNavigate();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const rootData = useRouteLoaderData<typeof rootLoader>('root');
  const systemName = rootData?.systemName ?? 'SwellFox Waves';

  const canAccessKBase = userHasPermission(user, 'kbase:read:any');
  const canAccessAdmin = userHasPermission(user, 'admin:read:any');
  const canAccessMetaTag = userHasPermission(user, 'cbmetatag:read:any');
  const canAccessQuizGen = userHasPermission(user, 'cbquiz:read:any');
  const hasAnyApp =
    canAccessKBase || canAccessAdmin || canAccessMetaTag || canAccessQuizGen;

  const toggleMobileMenu = () => {
    document.body.classList.toggle('mobile-menu-open');
  };

  return (
    <Header aria-label={systemName}>
      <SkipToContent />
      {section === 'Knowledge Base' && (
        <button
          onClick={toggleMobileMenu}
          className="md:hidden p-2 text-white hover:bg-gray-700 transition-colors border-0 bg-transparent cursor-pointer"
          aria-label="Toggle menu"
          type="button"
        >
          <Icon name="carbon/menu" size="lg" />
        </button>
      )}
      <HeaderName as={Link} to="/" prefix="" className="focus:border-0!">
        <div
          className={cn(
            'inline text-white',
            section ? 'text-neutral-orange-400' : '',
          )}
        >
          {systemName}
        </div>
        {section && (
          <span className="ml-2 text-neutral-blue-100">| {section}</span>
        )}
      </HeaderName>
      {/* <HeaderNavigation aria-label="Main navigation">
        <HeaderMenuItem href="/link1">Link 1</HeaderMenuItem>
        <HeaderMenuItem href="/link2">Link 2</HeaderMenuItem>
        <HeaderMenuItem href="/link3">Link 3</HeaderMenuItem>
        <HeaderMenu aria-label="Link 4" menuLinkName="Link 4">
          <HeaderMenuItem href="/link4/sub1">Sub-link 1</HeaderMenuItem>
          <HeaderMenuItem href="/link4/sub2">Sub-link 2</HeaderMenuItem>
          <HeaderMenuItem href="/link4/sub3">Sub-link 3</HeaderMenuItem>
        </HeaderMenu>
      </HeaderNavigation> */}
      <HeaderGlobalBar>
        {/* <HeaderGlobalAction aria-label="Search" tooltipAlignment="end">
          <Search size={20} />
        </HeaderGlobalAction> */}
        <ThemeSwitcherAction userPreference={userPreference} />
        {user && hasAnyApp && (
          <HeaderGlobalAction
            aria-label="App Switcher"
            tooltipAlignment="end"
            isActive={switcherOpen}
            onClick={() => setSwitcherOpen((o) => !o)}
          >
            <SwitcherIcon size={20} />
          </HeaderGlobalAction>
        )}
        {user && (
          <OverflowMenu
            renderIcon={() => {
              // Only show profile image if user actually has one uploaded
              if (user?.image?.objectKey) {
                const userImageSrc = getUserImgSrc(user.image.objectKey);
                return (
                  <img
                    src={userImageSrc}
                    alt="Profile"
                    className="h-6 w-6 rounded-full object-cover"
                  />
                );
              }

              return <UserAvatar size={20} />;
            }}
            size="lg"
            flipped
          >
            {user && (
              <OverflowMenuItem
                itemText={
                  <div className="text-left">
                    <div className="font-medium">
                      {user.name || user.username}
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-400">
                      @{user.username}
                    </div>
                  </div>
                }
                disabled
              />
            )}

            <OverflowMenuItem
              itemText="Profile"
              href="/settings/profile"
              className="no-underline outline-none! focus:outline-none!"
            />
            <OverflowMenuItem
              itemText="Logout"
              hasDivider
              onClick={() => {
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = '/logout';
                document.body.appendChild(form);
                form.submit();
              }}
            />
          </OverflowMenu>
        )}
      </HeaderGlobalBar>
      {user && hasAnyApp && (
        <HeaderPanel expanded={switcherOpen} aria-label="App Switcher">
          <Switcher aria-label="App Switcher" expanded={switcherOpen}>
            <SwitcherItem
              aria-label="Dashboard"
              onClick={() => {
                navigate('/');
                setSwitcherOpen(false);
              }}
            >
              Dashboard
            </SwitcherItem>
            {canAccessKBase && (
              <>
                <SwitcherDivider />
                <SwitcherItem
                  aria-label="Knowledge Base"
                  onClick={() => {
                    navigate('/kbase');
                    setSwitcherOpen(false);
                  }}
                >
                  Knowledge Base
                </SwitcherItem>
              </>
            )}
            {canAccessMetaTag && (
              <>
                <SwitcherDivider />
                <SwitcherItem
                  aria-label="Meta Tag Manager"
                  onClick={() => {
                    navigate('/cb-meta-tag-manager');
                    setSwitcherOpen(false);
                  }}
                >
                  Meta Tag Manager
                </SwitcherItem>
              </>
            )}
            {canAccessQuizGen && (
              <>
                <SwitcherDivider />
                <SwitcherItem
                  aria-label="Quiz Generator"
                  onClick={() => {
                    navigate('/cb-quiz-generator');
                    setSwitcherOpen(false);
                  }}
                >
                  Quiz Generator
                </SwitcherItem>
              </>
            )}
            {canAccessAdmin && (
              <>
                <SwitcherDivider />
                <SwitcherItem
                  aria-label="Admin"
                  onClick={() => {
                    navigate('/admin/users');
                    setSwitcherOpen(false);
                  }}
                >
                  Admin
                </SwitcherItem>
              </>
            )}
          </Switcher>
        </HeaderPanel>
      )}
    </Header>
  );
}
